// Dashboard Component - Main user hub
import { getXPProgress, formatNumber } from '../utils/notifications';
import { quests } from '../data/quests';
import Chart from 'chart.js/auto';
import gsap from 'gsap';

export function renderDashboard(appState) {
  const container = document.createElement('div');
  container.className = 'dashboard';

  const user = appState.user;
  // Handle both flat (localStorage) and nested (backend) structure
  const totalXP = user.totalXP !== undefined ? user.totalXP : (user.progress?.xp || 0);
  const xpData = getXPProgress(totalXP);
  // Prefer backend level logic if available
  const currentLevel = user.progress?.level || xpData.level || 1;

  const badgeCount = (user.badges?.length) || (user.progress?.badges?.length) || 0;
  const completedQuestCount = (user.completedQuests?.length) || (user.progress?.completedQuests?.length) || 0;

  container.innerHTML = `
    <div style="margin-bottom: var(--spacing-2xl);">
      <h1 class="kinetic-text" style="font-size: var(--font-size-4xl);">
        Welcome Back, <span class="text-gradient">${user.name || user.username || 'Explorer'}</span>! 👋
      </h1>
      <p style="color: var(--color-text-secondary); font-size: var(--font-size-lg);">
        Ready to continue your STEM journey?
      </p>
    </div>
    
    <!-- Stats Grid -->
    <div class="grid grid-3" style="margin-bottom: var(--spacing-2xl);">
      <!-- XP Progress Card -->
      <div class="card-brutal hover-lift">
        <h3 style="font-size: var(--font-size-xl); margin-bottom: var(--spacing-lg);">
          ⚡ Level Progress
        </h3>
        <div style="text-align: center; margin-bottom: var(--spacing-lg);">
          <div style="font-size: var(--font-size-5xl); font-weight: 900; font-family: var(--font-display); color: var(--color-primary);">
            ${currentLevel}
          </div>
          <div style="color: var(--color-text-secondary); font-size: var(--font-size-sm);">
            Current Level
          </div>
        </div>
        <div class="progress">
          <div class="progress-bar" style="width: ${xpData.progress}%;"></div>
          <div class="progress-xp">${formatNumber(xpData.currentXP)} / ${formatNumber(xpData.requiredXP)} XP</div>
        </div>
      </div>
      
      <!-- Badges Card -->
      <div class="card-brutal hover-lift">
        <h3 style="font-size: var(--font-size-xl); margin-bottom: var(--spacing-lg);">
          🏆 Badge Collection
        </h3>
        <div style="text-align: center; margin-bottom: var(--spacing-md);">
          <div style="font-size: var(--font-size-5xl); font-weight: 900; font-family: var(--font-display); color: var(--color-tertiary);">
            ${badgeCount}
          </div>
          <div style="color: var(--color-text-secondary); font-size: var(--font-size-sm);">
            Badges Earned
          </div>
        </div>
        <div style="font-size: var(--font-size-sm); color: var(--color-text-muted); text-align: center;">
          Keep completing quests to earn more!
        </div>
      </div>
      
      <!-- Quests Completed Card -->
      <div class="card-brutal hover-lift">
        <h3 style="font-size: var(--font-size-xl); margin-bottom: var(--spacing-lg);">
          🎯 Quests Completed
        </h3>
        <div style="text-align: center; margin-bottom: var(--spacing-md);">
          <div style="font-size: var(--font-size-5xl); font-weight: 900; font-family: var(--font-display); color: var(--color-success);">
            ${completedQuestCount}
          </div>
          <div style="color: var(--color-text-secondary); font-size: var(--font-size-sm);">
            Total Quests
          </div>
        </div>
        <div style="font-size: var(--font-size-sm); color: var(--color-text-muted); text-align: center;">
          ${completedQuestCount === 0 ? 'Start your first quest now!' : 'Great progress!'}
        </div>
      </div>
    </div>
    
    <!-- Main Content Grid -->
    <div class="grid grid-2">
      <!-- Recommended Quests -->
      <div class="card-brutal" style="height: fit-content;">
        <div class="card-header">
          <h2 class="card-title">📚 Recommended for You</h2>
          <p class="card-subtitle">Based on your interests and progress</p>
        </div>
        <div id="recommended-quests" class="flex flex-col gap-md">
          ${renderRecommendedQuests(user)}
        </div>
        <button class="btn btn-primary" style="width: 100%; margin-top: var(--spacing-lg);" id="view-all-quests">
          View All Quests →
        </button>
      </div>
      
      <!-- AI Insights & Performance -->
      <div class="card-brutal">
        <div class="card-header">
          <h2 class="card-title">🤖 AI Insights</h2>
          <p class="card-subtitle">Personalized recommendations from your AI tutor</p>
        </div>
        <div id="ai-insights" style="margin-bottom: var(--spacing-xl);">
          ${renderAIInsights(user)}
        </div>
        
        <div class="card-header" style="margin-top: var(--spacing-xl);">
          <h3 class="card-title" style="font-size: var(--font-size-xl);">📊 Performance Overview</h3>
        </div>
        <div style="position: relative; min-height: 250px;">
          <canvas id="performance-chart" style="max-height: 250px;"></canvas>
        </div>
      </div>
    </div>
  `;

  // Add event listeners
  setTimeout(() => {
    const viewAllBtn = container.querySelector('#view-all-quests');
    viewAllBtn.addEventListener('click', () => {
      import('../app').then(module => {
        module.navigateTo('quests', appState);
      });
    });


    // Add click listeners to recommended quests
    const recommendedCards = container.querySelectorAll('.recommended-quest-card');
    recommendedCards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const questId = card.getAttribute('data-quest-id');

        // Dynamic import to avoid circular dependencies
        import('./QuestView').then(module => {
          try {
            module.startQuest(questId, appState);
          } catch (err) {
            console.error('Error starting quest:', err);
          }
        });
      });
    });

    // Initialize performance chart
    initPerformanceChart(user);
  }, 100);

  return container;
}

function renderRecommendedQuests(user) {
  // Get all available quests
  const allQuests = Object.values(quests).flat();

  // Get completed quest IDs (handle both flat and nested structures)
  const completedList = user.completedQuests || user.progress?.completedQuests || [];
  const completedIds = completedList.map(q => typeof q === 'string' ? q : q.questId);

  // Filter out completed quests and get top 3 recommendations
  // (In a real app, this would use an algorithm based on interests/skill)
  const recommendations = allQuests
    .filter(quest => !completedIds.includes(quest.id))
    .slice(0, 3);

  // If all quests are completed
  if (recommendations.length === 0) {
    return `
      <div class="card" style="padding: var(--spacing-lg); text-align: center; color: var(--color-text-secondary);">
        <div style="font-size: var(--font-size-3xl); margin-bottom: var(--spacing-sm);">🎉</div>
        <p>You're all caught up!</p>
        <p style="font-size: var(--font-size-sm);">Check back later for more recommendations.</p>
      </div>
    `;
  }

  return recommendations.map(quest => `
    <div class="card hover-lift recommended-quest-card" data-quest-id="${quest.id}" style="padding: var(--spacing-md); cursor: pointer; background: var(--color-bg-elevated);">
      <div class="flex items-center gap-md">
        <div style="font-size: var(--font-size-3xl);">${quest.icon}</div>
        <div style="flex: 1;">
          <h4 style="font-size: var(--font-size-base); margin-bottom: var(--spacing-xs);">
            ${quest.title}
          </h4>
          <div class="flex items-center gap-sm" style="margin-top: var(--spacing-xs);">
            <span class="quest-difficulty ${quest.difficulty}">${quest.difficulty}</span>
            <span class="quest-xp">+${quest.xp} XP</span>
          </div>
        </div>
        <div style="color: var(--color-primary); font-size: var(--font-size-lg);">→</div>
      </div>
    </div>
  `).join('');
}

function renderAIInsights(user) {
  const insights = [
    {
      type: 'strength',
      emoji: '💪',
      title: 'Strong in Logic',
      message: 'Your problem-solving skills in mathematics are excellent!'
    },
    {
      type: 'opportunity',
      emoji: '🎯',
      title: 'Practice Opportunity',
      message: 'Try more physics quests to strengthen your understanding of mechanics.'
    },
    {
      type: 'recommendation',
      emoji: '🚀',
      title: 'Career Path',
      message: user.careerGoal ? `Your progress aligns well with becoming a ${user.careerGoal}!` : 'Complete your profile to get personalized career recommendations.'
    }
  ];

  return insights.map(insight => `
    <div class="flex gap-md" style="padding: var(--spacing-md); background: rgba(0, 240, 255, 0.05); border-left: 3px solid var(--color-primary); margin-bottom: var(--spacing-md);">
      <div style="font-size: var(--font-size-2xl);">${insight.emoji}</div>
      <div>
        <div style="font-weight: 700; margin-bottom: var(--spacing-xs);">${insight.title}</div>
        <div style="font-size: var(--font-size-sm); color: var(--color-text-secondary);">
          ${insight.message}
        </div>
      </div>
    </div>
  `).join('');
}

function initPerformanceChart(user) {
  const canvas = document.getElementById('performance-chart');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  // Map of interest names to chart labels and colors
  const subjectMap = {
    'Mathematics': {
      label: 'Math',
      color: 'rgba(0, 240, 255, 0.6)',
      borderColor: '#00f0ff',
      proficiency: 85
    },
    'Physics': {
      label: 'Physics',
      color: 'rgba(255, 0, 255, 0.6)',
      borderColor: '#ff00ff',
      proficiency: 72
    },
    'Chemistry': {
      label: 'Chemistry',
      color: 'rgba(255, 255, 0, 0.6)',
      borderColor: '#ffff00',
      proficiency: 65
    },
    'Computer Science': {
      label: 'CS',
      color: 'rgba(0, 255, 136, 0.6)',
      borderColor: '#00ff88',
      proficiency: 90
    }
  };

  // Get user's interests from profile
  const userInterests = user.interests || user.profile?.interests || [];

  // Filter chart data based on user interests
  const labels = [];
  const proficiencyData = [];
  const backgroundColors = [];
  const borderColors = [];

  // Helper to calculate score
  const getSubjectScore = (subjectName) => {
    // Map display name to data key (e.g. "Computer Science" -> "computer-science")
    const key = subjectName.toLowerCase().replace(' ', '-');
    const subjectQuests = quests[key] || [];
    const totalQuests = subjectQuests.length;

    if (totalQuests === 0) return 0;

    const completedList = user.completedQuests || (user.progress && user.progress.completedQuests) || [];
    const completedIds = new Set(completedList.map(q => typeof q === 'string' ? q : q.questId));

    const completedCount = subjectQuests.filter(q => completedIds.has(q.id)).length;
    return Math.round((completedCount / totalQuests) * 100);
  };

  userInterests.forEach(interest => {
    if (subjectMap[interest]) {
      const subject = subjectMap[interest];
      const realScore = getSubjectScore(interest);

      labels.push(subject.label);
      proficiencyData.push(realScore);
      backgroundColors.push(subject.color);
      borderColors.push(subject.borderColor);
    }
  });

  // If no interests, show a message
  if (labels.length === 0) {
    canvas.parentElement.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; text-align: center; padding: var(--spacing-md); color: var(--color-text-secondary);">
        <div style="font-size: var(--font-size-3xl); margin-bottom: var(--spacing-sm);">📊</div>
        <h4 style="color: var(--color-text-primary); margin-bottom: var(--spacing-xs);">Chart Waiting for Data</h4>
        <p style="font-size: var(--font-size-sm); opacity: 0.8; max-width: 80%;">
          This chart will show your proficiency in your selected interests (Math, Physics, etc.) as you complete quests.
        </p>
      </div>
    `;
    return;
  }

  // Sample performance data
  const data = {
    labels: labels,
    datasets: [{
      label: 'Proficiency %',
      data: proficiencyData,
      backgroundColor: backgroundColors,
      borderColor: borderColors,
      borderWidth: 3
    }]
  };

  new Chart(ctx, {
    type: 'bar',
    data: data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#a0aec0'
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#a0aec0'
          }
        }
      }
    }
  });
}
